// ---------------------------------------------------------------------------
// trajectoryDeeplink — encode and decode an ExecutionTrajectory carried in a
// debug-page deeplink.
//
// Link format: /debug?view=trajectory#trajectory=<value>
//
// <value> is base64url (RFC 4648 §5, padding optional) of the gzip-compressed
// UTF-8 trajectory JSON. Uncompressed base64 JSON is also accepted; gzip is
// detected from its magic bytes.
//
// The value lives in the URL fragment because a trajectory is typically tens
// of kilobytes even when compressed. Fragments are never sent to the server,
// so they avoid request-line limits and keep trajectory content out of server
// and proxy logs. A `?trajectory=` query parameter is also read for small
// payloads.
// ---------------------------------------------------------------------------

export const TRAJECTORY_DEEPLINK_PARAM = 'trajectory';

const GZIP_MAGIC = [0x1f, 0x8b] as const;

type Bytes = Uint8Array<ArrayBuffer>;

async function pipeThrough(
  bytes: Bytes,
  stream: CompressionStream | DecompressionStream,
): Promise<Bytes> {
  const writer = stream.writable.getWriter();
  const reader = stream.readable.getReader();
  const chunks: Uint8Array[] = [];
  // Write and read concurrently to avoid backpressure deadlock.
  await Promise.all([
    writer.write(bytes).then(() => writer.close()),
    (async () => {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
      }
    })(),
  ]);
  const result = new Uint8Array(chunks.reduce((n, c) => n + c.length, 0));
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

function base64UrlToBytes(value: string): Bytes {
  // URLSearchParams decodes '+' as a space, so restore it for standard base64.
  const base64 = value
    .trim()
    .replace(/ /g, '+')
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** Reads the trajectory deeplink value from the URL fragment, then the query. */
export function readTrajectoryDeeplinkValue(location: {
  hash: string;
  search: string;
}): string | null {
  const fromHash = new URLSearchParams(location.hash.replace(/^#/, '')).get(
    TRAJECTORY_DEEPLINK_PARAM,
  );
  if (fromHash) return fromHash;
  const fromQuery = new URLSearchParams(location.search).get(
    TRAJECTORY_DEEPLINK_PARAM,
  );
  return fromQuery === '' ? null : fromQuery;
}

/**
 * Decodes a deeplink value into trajectory JSON text.
 * Throws when the value is not valid base64 or not valid gzip.
 */
export async function decodeTrajectoryDeeplink(value: string): Promise<string> {
  let bytes: Bytes;
  try {
    bytes = base64UrlToBytes(value);
  } catch {
    throw new Error('The trajectory link is not valid base64.');
  }
  if (bytes[0] === GZIP_MAGIC[0] && bytes[1] === GZIP_MAGIC[1]) {
    try {
      bytes = await pipeThrough(bytes, new DecompressionStream('gzip'));
    } catch {
      throw new Error('The trajectory link could not be decompressed.');
    }
  }
  return new TextDecoder().decode(bytes);
}

/** Encodes trajectory JSON text as a gzip + base64url deeplink value. */
export async function encodeTrajectoryDeeplink(json: string): Promise<string> {
  const compressed = await pipeThrough(
    new TextEncoder().encode(json),
    new CompressionStream('gzip'),
  );
  return bytesToBase64Url(compressed);
}
